import React, { useEffect, useState, useCallback } from 'react';
import { View, ScrollView, RefreshControl } from 'react-native';
import { observer } from 'mobx-react';
import { RewardedAdEventType, AdEventType } from 'react-native-google-mobile-ads';

import Modal from './shared/Modal';
import Text from './shared/Text';
import Button from './shared/Button';
import DroppedModal from './DroppedModal';
import heroStore from '../stores/hero';
import appStore from '../stores/app';
import adsStore from '../lib/ads';
import { getQuestsCatalog, getWarriorLogs, getWarriorBonuses } from '../lib/dbCalls';
import { settledBonus, settledBonusAd } from '../lib/apiCalls';
import { action, getLocalValue, t } from '../utils/appUtils';
import { getUtcDayKey } from '../utils/utils';
import {
  getDailyQuests,
  isQuestCondition,
  questConditionId,
  questProgress,
} from '../utils/questUtils';
import { IS_WEB } from '../lib/appConstants';
import {
  BASE_COLOR,
  GREEN,
  GOLDEN100,
  GOLDEN200,
  MARGIN_5,
  MARGIN_10,
  MARGIN_15,
  MARGIN_25,
} from './styleConstants';
import { BonusType, QuestToType, WarriorBonusType, WarriorLogsType } from '../lib/types';

type Props = { onHide: () => void };

type QuestChest = WarriorBonusType & { _originalIndex: number };

/** Client-side preview of the doubled reward; the backend does the real scaling. */
const doubleBonus = (bonus: BonusType): BonusType => ({
  money: bonus.money ? bonus.money * 2 : undefined,
  things: bonus.things ? [...bonus.things, ...bonus.things] : undefined,
});

const ProgressBar: React.FC<{ current: number; target: number }> = ({ current, target }) => {
  const ratio = target > 0 ? Math.max(0, Math.min(1, current / target)) : 0;
  return (
    <View
      style={{
        height: 10,
        borderRadius: 5,
        backgroundColor: GOLDEN100,
        overflow: 'hidden',
      }}
    >
      <View
        style={{
          width: `${ratio * 100}%`,
          height: '100%',
          borderRadius: 5,
          backgroundColor: GREEN,
        }}
      />
    </View>
  );
};

const QuestsModal = observer(function QuestsModal({ onHide }: Props) {
  const [quests, setQuests] = useState<QuestToType[]>([]);
  const [chests, setChests] = useState<QuestChest[]>([]);
  const [logs, setLogs] = useState<WarriorLogsType>({});
  const [fetching, setFetching] = useState(true);
  const [rewardBonus, setRewardBonus] = useState<BonusType | null>(null);
  const [adBusyId, setAdBusyId] = useState<string | null>(null);

  const todayKey = getUtcDayKey(Date.now());
  const adsAvailable = !IS_WEB && adsStore.adsReady;

  const fetch = useCallback(async () => {
    setFetching(true);
    try {
      const [catalog, warriorLogs, bonuses] = await Promise.all([
        getQuestsCatalog(),
        getWarriorLogs(heroStore.hero.id),
        getWarriorBonuses(heroStore.hero.id),
      ]);
      const day = getUtcDayKey(Date.now());
      const questChests: QuestChest[] = bonuses
        .map((b, i) => ({ ...b, _originalIndex: i }))
        .filter((b) => isQuestCondition(b.condition));
      setQuests(getDailyQuests(catalog, day));
      setLogs(warriorLogs);
      setChests(questChests);
    } catch (e) {
      setQuests([]);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const markSettled = (chest: QuestChest) =>
    setChests((prev) => prev.map((c) => (c === chest ? { ...c, settled: true } : c)));

  const handleClaim = (chest: QuestChest) => {
    setRewardBonus(chest.bonus);
    markSettled(chest);
    settledBonus(chest._originalIndex);
  };

  // Watch a rewarded ad, then settle the same chest at 2× (backend-enforced).
  const handleWatchAdDouble = (chest: QuestChest) => {
    setAdBusyId(chest.condition);
    const ad = adsStore.loadRewarded();

    const subLoaded = ad.addAdEventListener(RewardedAdEventType.LOADED, () => {
      ad.show();
    });
    const subEarned = ad.addAdEventListener(RewardedAdEventType.EARNED_REWARD, async () => {
      try {
        await settledBonusAd(chest._originalIndex);
        markSettled(chest);
        setRewardBonus(doubleBonus(chest.bonus));
      } catch (e) {
        appStore.showMessage({ type: 'error', text: t('somethingWrongError') });
      }
    });
    const subFailed = ad.addAdEventListener(AdEventType.ERROR, () => {
      appStore.showMessage({ type: 'error', text: t('ads.notAvailable') });
      setAdBusyId(null);
    });
    const subClosed = ad.addAdEventListener(AdEventType.CLOSED, () => {
      setAdBusyId(null);
      adsStore.consumeRewarded();
      subLoaded();
      subEarned();
      subFailed();
      subClosed();
    });
  };

  return (
    <Modal title={t('quests.title')} onHide={onHide}>
      <View style={{ marginTop: 40, marginBottom: 25, flex: 1 }}>
        <ScrollView
          refreshControl={
            <RefreshControl
              tintColor={BASE_COLOR}
              refreshing={fetching}
              onRefresh={() => action(fetch)}
            />
          }
        >
          <View style={{ marginHorizontal: MARGIN_25 }}>
            <Text style={{ fontStyle: 'italic', marginBottom: MARGIN_15 }}>
              {t('quests.resetsDaily')}
            </Text>

            {quests.length === 0 ? (
              !fetching ? <Text>{t('quests.empty')}</Text> : null
            ) : (
              quests.map((quest) => {
                const condition = questConditionId(quest.id, todayKey);
                const chest = chests.find((c) => c.condition === condition);
                const current = questProgress(quest, logs);
                const claimable = Boolean(chest && !chest.settled);
                const claimed = Boolean(chest && chest.settled);

                return (
                  <View
                    key={quest.id}
                    style={{
                      marginBottom: MARGIN_15,
                      paddingBottom: MARGIN_10,
                      borderBottomWidth: 1,
                      borderBottomColor: GOLDEN100,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: MARGIN_5,
                      }}
                    >
                      <Text bold style={{ flex: 1 }}>
                        {getLocalValue(quest, 'label')}
                      </Text>
                      {claimable ? (
                        <Button onPress={() => handleClaim(chest as QuestChest)}>
                          {t('quests.claim')}
                        </Button>
                      ) : claimed ? (
                        <Text style={{ color: GREEN }}>{t('quests.claimed')}</Text>
                      ) : (
                        <Text style={{ color: GOLDEN200 }}>
                          {current}/{quest.target}
                        </Text>
                      )}
                    </View>
                    <Text style={{ fontSize: 12, marginBottom: MARGIN_5 }}>
                      {getLocalValue(quest, 'description')}
                    </Text>
                    <ProgressBar
                      current={claimable || claimed ? quest.target : current}
                      target={quest.target}
                    />
                    {claimable && adsAvailable ? (
                      <View style={{ marginTop: MARGIN_10, alignItems: 'flex-start' }}>
                        <Button
                          onPress={() => handleWatchAdDouble(chest as QuestChest)}
                          disabled={Boolean(adBusyId)}
                        >
                          {t('quests.watchAdDouble', {
                            money: ((chest as QuestChest).bonus.money || 0) * 2,
                          })}
                        </Button>
                      </View>
                    ) : null}
                  </View>
                );
              })
            )}
          </View>
        </ScrollView>
      </View>

      {rewardBonus ? (
        <DroppedModal bonus={rewardBonus} onHide={() => setRewardBonus(null)} />
      ) : null}
    </Modal>
  );
});

export default QuestsModal;
